<template>
  <div class="mt-4">
    <h2 class="text-primary">Credit Score Report for {{ member_id }} | {{ name }}</h2>
    <hr>

    <!-- VueSpeedometer displaying the credit score -->
    <div class="row">
      <div class="col-6 pe-5">
        <center style="position: relative;">
          <h3 class="mb-2">Credit Score Meter</h3>
          <VueSpeedMeter :customCurrentValue="scaledCreditScore" :customStyle="{
            size: 350,
            mainBackgroundColor: '#FAF9F6',
            borderColor: '#130F0B',
            scaleColor: scaleColor,
            scaleValuesColor: scaleColor,
            scaleStartValue: 299,
            scaleStep: 46,
            needleColor: '#3F4140',
            needleCircleColor: scaleColor,
            needleCircleBorderColor: '#545454'
          }" :needleAnimation="needleAnimation" :shadowFilter="false" />
          <div class="h2 text-center mb-0 ms-3" style="position: relative; margin-top: -80px;"
            :style="{ color: scaleColor }">
            {{ scaledCreditScore }}
          </div>
          <div class="ms-3" style="position: relative; margin-top: -5px;">
            {{ comment }}
          </div>
        </center>
        <br><br>
        <div class="score-key">
          <hr>
          <h3>Credit Score Interpretation</h3>
          <ul class="fw-bold ms-2">
            <li style="color: #d62328">300 - 528: Poor</li>
            <li style="color: #ee7726 ">529 - 650: Low</li>
            <li style="color: #e6c61d">651 - 696: Fair</li>
            <li style="color: #b4b436">697 - 717: Good</li>
            <li style="color: #5ea744">718 - 743: Very Good</li>
            <li style="color: #108c59">744 - 850: Excellent</li>
          </ul>
        </div>

      </div>
      <div class="col">
        <!-- Breakdown of the credit score -->
        <div class="score-details">
          <h3>Score Breakdown</h3>
          <ul>
            <li v-for="(param, index) in parameters" :key="index" class="row">
              <strong>{{ param.name }}:</strong>
              <div class="col-3">{{ param.value }} points
              </div>
              <div class="col text-primary"> <span> (Max: {{ param.maxPoints }} points)</span>
              </div>
            </li>
          </ul>
        </div>

        <!-- Total score -->
        <div class="total-score">
          <h5 class="text-primary">Total Points: {{ totalPoints }} / 100</h5>
        </div>

        <!-- Recommendations -->
        <br><br>
        <h3>Recommendations</h3>
        <p class="mb-0">The member's loan limit should be set at:</p>
        <div>
          <div class="fs-1 fw-bold text-primary">{{ commas(loanLimit * saving_balance * creditPercentage) }}/=</div>
          <div class="fs-4 text-primary">{{ loanLimit * 100 * creditPercentage }}% of their Savings</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueSpeedMeter from "vue-speed-meter";

export default {
  components: {
    VueSpeedMeter,
  },
  data() {
    return {
      parameters: [
        { name: 'Loan Repayment History', value: 0, maxPoints: 35 },
        { name: 'Saving History', value: 0, maxPoints: 15 },
        { name: 'Character Assessment', value: 0, maxPoints: 15 },
        { name: 'Capacity to Repay', value: 0, maxPoints: 15 },
        { name: 'Duration of Membership', value: 0, maxPoints: 5 },
        { name: 'Meeting Attendance', value: 0, maxPoints: 5 },
        { name: 'Purpose of Loan', value: 0, maxPoints: 5 },
        { name: 'Availability of Bank Statements', value: 0, maxPoints: 5 }
      ],

      member_id: '',
      name: '',
      comment: 'Poor',
      saving_balance: 0,
      loanLimit: JSON.parse(localStorage.getItem('user')).settings.loanlimit,
    }
  },

  methods: {
    commas(x) { return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","); },

  },

  computed: {
    totalPoints() {
      return this.parameters.reduce((acc, param) => acc + param.value, 0);
    },

    scaledCreditScore() {
      let clampedTotal = Math.min(Math.max(this.totalPoints, 0), 100);
      let scaledValue = 300 + ((clampedTotal - 0) * (850 - 300)) / (100 - 0);

      return Math.floor(scaledValue);
    },

    scaleColor() {
      let color = '';
      if (this.scaledCreditScore >= 300 && this.scaledCreditScore <= 528) {
        color = '#d62328';
      } else if (this.scaledCreditScore >= 529 && this.scaledCreditScore <= 650) {
        color = '#ee7726';
      } else if (this.scaledCreditScore >= 651 && this.scaledCreditScore <= 696) {
        color = '#e6c61d';
      } else if (this.scaledCreditScore >= 697 && this.scaledCreditScore <= 717) {
        color = '#b4b436';
      } else if (this.scaledCreditScore >= 718 && this.scaledCreditScore <= 743) {
        color = '#5ea744';
      } else if (this.scaledCreditScore >= 744 && this.scaledCreditScore <= 850) {
        color = '#108c59';
      }
      return color
    },

    creditPercentage() {
      let percentage = 0;
      if (this.scaledCreditScore >= 300 && this.scaledCreditScore <= 528) {
        percentage = 0.3;
      } else if (this.scaledCreditScore >= 529 && this.scaledCreditScore <= 650) {
        percentage = 0.5;
      } else if (this.scaledCreditScore >= 651 && this.scaledCreditScore <= 696) {
        percentage = 0.7;
      } else if (this.scaledCreditScore >= 697 && this.scaledCreditScore <= 717) {
        percentage = 0.9;
      } else if (this.scaledCreditScore >= 718 && this.scaledCreditScore <= 743) {
        percentage = 1.0;
      } else if (this.scaledCreditScore >= 744 && this.scaledCreditScore <= 850) {
        percentage = 1.2;
      }
      return percentage
    }
  },

  mounted() {
    const member = this.$route.params.member;
    const query = this.$route.params.parameters;
    const saving_balance = this.$route.params.saving_balance;
    const comment = this.$route.params.comment;

    this.member_id = member.id;
    this.name = member.name;
    this.saving_balance = saving_balance;
    this.comment = comment;
    this.parameters.forEach(param => {
      if (query[param.name]) {
        param.value = parseInt(query[param.name], 10);
      }
    });
    // Print the page when the component is mounted
    setTimeout(() => { window.print(); window.onafterprint = this.$router.go(-1); }, 1000);
  }
};
</script>

<style scoped>
h2,
h3 {
  color: #333;
}

.score-details,
.total-score,
.score-key {
  margin-top: 20px;
}

ul {
  list-style: none;
  padding: 0;
}

li {
  margin-bottom: 10px;
}
</style>